import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator, TextArea } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import {
    setChangeRequestParticipantOffer,
    ChangeRequestParticipantOffer,
    ChangeRequestParticipantLine,
} from '@/services/change-requests';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import ChangeRequestAttachmentList from './ChangeRequestAttachmentList';
import {
    ChangeRequestParticipantInfo,
    ChangeRequestAttachment,
    ProjectFeeSet,
    ChangeRequestParticipantLineInfo,
    BatchedChanges,
    ChangeRequest,
} from '@/types';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { getProjectFeeSets } from '@/services/fees';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { PriceRowEditor, PriceRowEditorFooter } from '@/seller/components/PriceRow';
import { NoAvailableFeesAlert } from '../fees';
import { ValidationScope, ValidationSummary } from '@/components/forms/validation';
import { useValidationManager } from '@/utils/hooks';
import InfoIconWithTooltip from '../InfoIconWithTooltip';
import Banner from '@/components/Banner';
import { t } from '@lingui/core/macro';
import ReadyToggleCard from '@/components/ReadyToggleCard';

interface ChangeRequestParticipantOfferDialogProps {
    participant: ChangeRequestParticipantInfo | null;
    changeRequest: ChangeRequest;
    show: boolean;
    onClose(): void;
    onSave?(change: BatchedChanges<ChangeRequestParticipantInfo>): void;
    onReadyStateChange?(participant: ChangeRequestParticipantInfo, checked: boolean): void;
}

function lineInfoToLine(info: ChangeRequestParticipantLineInfo): ChangeRequestParticipantLine {
    return {
        id: info.id,
        price: info.priceCalculation!.basePrice,
        description: info.description,
        projectFeeSetId: info.feeSetInfo.projectFeeSetId!,
        itemCategoryId: info.itemCategoryId,
        tags: info.tags,
    };
}

const ChangeRequestParticipantOfferDialog: React.FC<ChangeRequestParticipantOfferDialogProps> = ({
    participant,
    show,
    onClose,
    onSave,
    changeRequest,
    onReadyStateChange,
}) => {
    const [note, setNote] = useState('');
    const [attachments, setAttachments] = useState<ChangeRequestAttachment[]>([]);
    const [offerLines, setOfferLines] = useState<ChangeRequestParticipantLine[]>([]);
    const [saving, setSaving] = useState(false);
    const [isReadyToSend, setIsReadyToSend] = useState(false);

    const [projectFeeSets, setProjectFeeSets] = useState<ProjectFeeSet[] | null>(null);
    const validationManager = useValidationManager();

    const activeProject = useSelector((state: IState) => state.global.activeProject!);
    const projectId = activeProject.id;
    const user = useSelector((state: IState) => state.global.user);

    const isAdminOverriding = participant?.userId != user?.userId;

    const getEmptyOfferLine = useCallback((): ChangeRequestParticipantLine => {
        const defaultFeeSet = projectFeeSets!.find((x) => x.isDefault) || projectFeeSets![0];

        return {
            id: null,
            description: '',
            price: null,
            itemCategoryId: undefined,
            projectFeeSetId: defaultFeeSet ? defaultFeeSet.id : 0,
            tags: [],
        };
    }, [projectFeeSets]);

    const handleDialogOpen = async () => {
        if (!projectFeeSets) {
            setProjectFeeSets(await getProjectFeeSets(projectId));
        }
    };

    useEffect(() => {
        if (participant && projectFeeSets) {
            const { offerNote, offerAttachments, offerLines, isReadyToSend } = participant;
            setNote(offerNote || '');
            setAttachments([...offerAttachments]);
            setIsReadyToSend(isReadyToSend || false);
            if (!offerLines || offerLines.length === 0) {
                setOfferLines([getEmptyOfferLine()]);
            } else {
                setOfferLines(offerLines.map(lineInfoToLine));
            }
        }
    }, [participant, projectFeeSets, getEmptyOfferLine]);

    const handleSaveClick = async () => {
        if (validationManager.validate().length > 0) {
            return;
        }

        const data: ChangeRequestParticipantOffer = {
            note,
            attachments,
            lines: offerLines.map((x) => ({ ...x, priceCalculation: undefined })),
            participantId: isAdminOverriding ? participant!.id : undefined,
        };

        setSaving(true);

        try {
            var result = await setChangeRequestParticipantOffer(changeRequest.id, data);

            triggerHandler(onSave, BatchedChanges.single<ChangeRequestParticipantInfo>(participant!.id, result));
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleLineChange = (newItem: ChangeRequestParticipantLine, oldItem: ChangeRequestParticipantLine) => {
        setOfferLines(arrayReplace(offerLines, oldItem, newItem));
    };

    const handleLineAdd = () => {
        setOfferLines([...offerLines, getEmptyOfferLine()]);
    };

    const handleLineRemove = (item: ChangeRequestParticipantLine) => {
        setOfferLines(offerLines.filter((x) => x !== item));
    };

    const renderBody = () => {
        if (!offerLines || !projectFeeSets) {
            return <LoadingIndicator />;
        }

        if (projectFeeSets.length === 0) {
            return <NoAvailableFeesAlert />;
        }

        return (
            <ValidationScope validationManager={validationManager}>
                {isAdminOverriding && (
                    <Banner type="danger">
                        <Trans>
                            <strong>Please note</strong> you are overriding <strong> {participant?.displayName}</strong>{' '}
                            offer lines.
                        </Trans>
                    </Banner>
                )}
                <div>
                    {offerLines.map((row, i) => (
                        <PriceRowEditor
                            key={i}
                            showLabels={i === 0}
                            value={row}
                            onChange={(newValue) => handleLineChange({ ...row, ...newValue }, row)}
                            onDeleteClick={() => handleLineRemove(row)}
                            projectFeeSets={projectFeeSets}
                            rootItemCategoryFilter={(cat) =>
                                activeProject.permissions.categoryPermissions[cat.id]?.canUseInChangeRequests ?? false
                            }
                        />
                    ))}

                    <PriceRowEditorFooter rows={offerLines} onAddRowClick={handleLineAdd} />
                </div>
                <div>
                    <Banner type="info">
                        <Trans>Use this field to explain the pricing. It will be visible to the buyer.</Trans>
                    </Banner>
                </div>

                <div className="form-group">
                    <label>
                        <Trans>Add quote messages</Trans>
                        <InfoIconWithTooltip content={<Trans>You may include a description for your quote</Trans>} />
                    </label>
                    <TextArea
                        autoResize={true}
                        value={note}
                        name="note"
                        className="form-control"
                        placeholder={t`Notes`}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </div>

                <div className="form-group">
                    <label>
                        <Trans>Attachments</Trans>
                    </label>
                    <ChangeRequestAttachmentList
                        useUploadWizard
                        residentialUnitId={changeRequest.residentialUnitId}
                        value={attachments}
                        onChange={(newValue) => setAttachments(newValue)}
                        showVisibilityFlags={true}
                        ownerProfileId={participant?.profileId}
                        allowPaste
                    />
                </div>

                <ValidationSummary />
            </ValidationScope>
        );
    };

    return (
        <Modal
            isOpen={show}
            toggle={handleCancelClick}
            onOpen={handleDialogOpen}
            size="xl">
            <ModalHeader>
                {participant?.offerPrice != null ? <Trans>Update quote</Trans> : <Trans>Give quote</Trans>}
            </ModalHeader>
            <ModalBody>{renderBody()}</ModalBody>
            <ReadyToggleCard
                checked={isReadyToSend}
                width="209px"
                onChange={(checked) => {
                    setIsReadyToSend(checked);
                    participant && triggerHandler(onReadyStateChange, participant, checked);
                }}
            />
            <ModalFooter>
                <ButtonBar margins="none">
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        {projectFeeSets && projectFeeSets.length > 0 && (
                            <Button variant="primary" onClick={handleSaveClick} loading={saving}>
                                <Trans>Save and exit</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </ModalFooter>
        </Modal>
    );
};

export default ChangeRequestParticipantOfferDialog;
